/* eslint-disable no-inline-comments */
import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'
import { connect } from 'react-redux'
import IconButton from '@material-ui/core/IconButton'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import { filterZZZ, getSortedCompanyValueLabels } from './categoryLabelUtils'

// Base sizing was originally 65% of MUI's stock Tab defaults (minHeight 48 /
// font 14px), then bumped up a step for readability. Mobile (xs breakpoint)
// still gets somewhat more compact padding/height, but labels are NEVER
// truncated - tabs size to fit their full text, and the row scrolls
// (drag, swipe, or the arrow buttons) to reach whatever doesn't fit.
const styles = theme => ({
  root: {
    width: '100%',
    marginTop: 8
  },
  // Each tab row is [prev arrow][scrollable tab strip][next arrow].
  tabRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%'
  },
  // This div IS the scrollable element AND the tab list itself (plain
  // HTML/CSS, not MUI's <Tabs> - see the SCROLLING doc comment below for
  // why). display:flex lays the tab buttons out in a row; overflowX:auto
  // is what makes that row scrollable once it's wider than this box.
  tabsScrollWrapper: {
    display: 'flex',
    alignItems: 'center',
    flex: '1 1 auto',
    minWidth: 0, // required so this flex child can actually be smaller than its content and scroll
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch', // momentum scrolling on older iOS
    cursor: 'grab',
    userSelect: 'none', // prevents accidental text selection while mouse-dragging across tab labels
    paddingRight: 12, // breathing room so the last tab isn't flush against the edge
    // Hide the (now functionally redundant, since we have arrows + drag)
    // native scrollbar for a cleaner look, while the element stays scrollable.
    scrollbarWidth: 'none', // Firefox
    '&::-webkit-scrollbar': {
      display: 'none' // Chrome / Safari
    }
  },
  scrollButton: {
    flex: '0 0 auto',
    padding: 2,
    touchAction: 'manipulation'
  },
  scrollIcon: {
    fontSize: 18
  },
  subScrollIcon: {
    fontSize: 15
  },
  // Real browser-tab look: a visible border and rounded top edge on every
  // tab, so the outline shows even when unselected - plain <button>
  // elements, styled directly (no MUI Tab component underneath).
  tabButton: {
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    boxSizing: 'border-box', // so removing the selected tab's bottom border doesn't shift its total height by 1px
    font: 'inherit',
    minHeight: 34,
    flex: '0 0 auto',
    padding: '2px 9px',
    margin: 0,
    marginRight: 4, // small gap so the rounded corners actually read as separate tab shapes
    fontSize: 11,
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
    color: 'rgba(0, 0, 0, 0.72)',
    border: '1px solid rgba(0, 0, 0, 0.25)',
    borderRadius: '4px 4px 0 0',
    backgroundColor: '#f0f0f0',
    cursor: 'pointer',
    outline: 'none',
    '&:focus-visible': {
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}`
    },
    // Mobile: a little more compact, but NOT width-capped - full label
    // text always stays visible, the row just scrolls further.
    [theme.breakpoints.down('xs')]: {
      minHeight: 28,
      padding: '2px 7px',
      marginRight: 3,
      fontSize: 10
    }
  },
  tabButtonSelected: {
    backgroundColor: '#ffffff',
    borderColor: 'rgba(0, 0, 0, 0.4)',
    // No border-bottom at all (width 0, not just a matching color) - nothing
    // left to color-match against whatever the actual surrounding
    // background turns out to be, so no chance of a faint mismatch line.
    borderBottomWidth: '0 !important',
    // Also dropped: the boxShadow this had before. A blurred shadow can
    // still bleed a faint edge downward even with a negative y-offset,
    // and it was a plausible second source of the line that persisted
    // after the border fix alone.
    zIndex: 1,
    position: 'relative'
  },
  topTabSelectedText: {
    color: theme.palette.primary.main
  },
  subTabsWrap: {
    marginTop: 1
  },
  subTabButton: {
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    boxSizing: 'border-box',
    font: 'inherit',
    minHeight: 27,
    flex: '0 0 auto',
    padding: '1px 7px',
    margin: 0,
    marginRight: 3,
    fontSize: 10,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    color: 'rgba(0, 0, 0, 0.72)',
    border: '1px solid rgba(0, 0, 0, 0.2)',
    borderRadius: '3px 3px 0 0',
    backgroundColor: '#f5f5f5',
    cursor: 'pointer',
    outline: 'none',
    '&:focus-visible': {
      boxShadow: `0 0 0 2px ${theme.palette.secondary.main}`
    },
    [theme.breakpoints.down('xs')]: {
      minHeight: 22,
      padding: '1px 6px',
      marginRight: 3,
      fontSize: 9
    }
  },
  subTabSelectedText: {
    color: theme.palette.secondary.main
  }
})

/**
 * CategoryTabs
 *
 * A purely ADDITIVE, alternative UI for selecting competency / sub-competency.
 * It does NOT introduce a new state path - it drives selection through the
 * exact same callbacks the existing dropdowns (CompSelect / SubCompSelect)
 * already call: handleCompetencyChange(id, name) and
 * handleSubCompetencyChange(id, name) on CategoriesSelect.
 *
 * Because of that, this component requires ZERO changes to CategoriesSelect's
 * existing state, lifecycle methods, or the dropdown component tree
 * (CompSelect -> CompetencyOptions, SubCompSelect -> SubCompetencyOptions).
 * Those keep working completely autonomously.
 *
 * Data is read-only from Redux, never dispatched from here:
 *   - state.pldp.companyValues            (top-level competency tabs)
 *   - state.subcompetenciesList.subcompetencies_list[competencyid]  (sub tabs)
 *
 * Both slices are kept fresh by the *existing* CompetencyOptions /
 * SubCompetencyOptions components, which are always mounted alongside this
 * one (they're siblings inside CategoriesSelect) and already hold live
 * Firebase `.on('value', ...)` listeners. CategoryTabs deliberately does not
 * dispatch getCompanyValues / fetchSubCompetencies itself, to avoid a
 * redundant second fetch every time CategoriesSelect mounts.
 *
 * Sub-tab fetching: when a competency tab is clicked, this component calls
 * onSelectCompetency(id, name) -> CategoriesSelect.handleCompetencyChange ->
 * sets state.competencyid -> flows down as a prop to SubCompSelect ->
 * SubCompetencyOptions.componentDidUpdate, which is what actually triggers
 * fetchSubCompetencies(compid). So sub-tab data arrives on the same timing
 * as it does for the dropdown today, whether the click originated here or
 * on the dropdown.
 *
 * SCROLLING / NAVIGATION (history, for future-me):
 * Multiple earlier attempts tried to make MUI's <Tabs>/<Tab> components
 * scroll reliably: its built-in scroll-button mechanism was unreliable on
 * mobile; its built-in "scroll the newly-selected tab into view" behavior
 * caused a real mobile freeze bug (a JS scroll animation colliding with an
 * in-progress touch gesture left the scroll container stuck until reload);
 * switching to variant='standard' to avoid that dropped an internal
 * flex-shrink:0 override MUI normally applies, causing every tab to
 * compress to fit instead of overflowing; overriding that reintroduced an
 * internal 'fixed' class MUI applies to the scroller under variant=
 * 'standard' that forces overflow-x:hidden, clipping the tabs before they
 * could ever reach an outer scrollable wrapper. Each fix was correct for
 * what it targeted, but MUI's Tabs component has enough internal,
 * version-specific, undocumented-to-us behavior layered together that
 * fighting it one class at a time kept surfacing a new layer underneath.
 *
 * Current approach: don't use MUI's <Tabs>/<Tab> at all for the scrollable
 * rendering. Each row is plain HTML - a <div style="display:flex;
 * overflow-x:auto"> directly containing plain <button> elements, styled to
 * look identical to before. This is about as well-tested and predictable
 * as browser behavior gets, with zero dependency on any MUI Tabs internal
 * class or lifecycle behavior:
 *   - That wrapper div (topTabsWrapperRef / subTabsWrapperRef) IS the
 *     scrollable element - no nested MUI-managed scroller/flexContainer
 *     layers to fight.
 *   - Prev/next IconButtons use a single plain onClick that nudges the
 *     wrapper's own scrollLeft directly.
 *   - Desktop mouse users can click-and-drag anywhere on the tab strip to
 *     pan it (gated to pointerType === 'mouse'; touch/pen get native
 *     swipe-scrolling instead, since the wrapper is a normal overflow-x
 *     container). Pointer capture is deliberately deferred until real drag
 *     movement is confirmed, not acquired on every mousedown - capturing
 *     unconditionally caused the browser to retarget every click's
 *     synthesized 'click' event to the wrapper div instead of the actual
 *     button underneath it, silently breaking ordinary tab selection by
 *     click. The same movement threshold also drives an onClickCapture
 *     guard that swallows the click if the pointer moved past it before
 *     release, so a genuine drag-then-release doesn't also fire an
 *     accidental tab selection.
 *   - After a tab IS selected, element.scrollIntoView({inline:'nearest',
 *     block:'nearest'}) is called on the clicked button (via
 *     event.currentTarget) to reproduce the old "shift a partially-visible
 *     tab into view" nicety, using the plain native DOM API rather than
 *     any custom or component-internal animation.
 *
 * KNOWN GAP (not yet handled - flagging rather than guessing):
 * CompetencyOptions renders an extra "All Sectors" (__ALL__) MenuItem when
 * filterMode is true, which ThoughtsView's usage of CategoriesSelect always
 * is. There is currently no equivalent "All" tab here, so a user relying on
 * tabs alone (once dropdowns are eventually turned off) would have no way to
 * clear back to the unfiltered "All Sectors" state. Needs a decision before
 * the dropdowns can safely be hidden.
 *
 * ASSUMPTION: sub-competencies are expected to be exactly 4 entries per
 * competency (per direction), so this renders whatever is present in
 * subcompetencies_list[competencyid] without padding/truncating to 4 - if a
 * competency's real data has more or fewer, that will show up here as-is
 * rather than being silently hidden.
 */
class CategoryTabs extends React.Component {
  topTabsWrapperRef = React.createRef()
  subTabsWrapperRef = React.createRef()

  // One drag-controller object per scrollable row, created once (class
  // field initializers run once per instance, not per render) so the
  // closured drag state (isPressed/startX/etc.) persists correctly across
  // re-renders without needing React state or re-renders of its own.
  topDrag = this.createDragController(this.topTabsWrapperRef)
  subDrag = this.createDragController(this.subTabsWrapperRef)

  // Builds a self-contained set of pointer handlers for mouse-drag-to-scroll
  // on one row. pointerType is checked so this only ever activates for an
  // actual mouse - touch/pen fall through to native swipe-scrolling on the
  // wrapper div (a plain overflow-x: auto element).
  createDragController(wrapperRef) {
    const DRAG_THRESHOLD = 4 // px of movement before we treat this as a real drag, not a click

    let isPressed = false
    let hasCaptured = false
    let startX = 0
    let startScrollLeft = 0
    let movedDistance = 0
    let pointerId = null

    const onPointerDown = event => {
      if (event.pointerType !== 'mouse') return
      if (!wrapperRef.current) return
      isPressed = true
      hasCaptured = false
      movedDistance = 0
      startX = event.clientX
      startScrollLeft = wrapperRef.current.scrollLeft
      pointerId = event.pointerId
      // IMPORTANT: deliberately do NOT call setPointerCapture here, on
      // every press. Browsers retarget the resulting synthesized 'click'
      // event to whichever element holds pointer capture - so capturing
      // unconditionally on every mousedown was silently redirecting every
      // click away from the actual tab button to this wrapper div instead.
      // Capture is only acquired below, once real drag movement is
      // confirmed - a plain click never triggers it at all.
    }

    const onPointerMove = event => {
      if (!isPressed || !wrapperRef.current) return
      const dx = event.clientX - startX
      movedDistance = Math.max(movedDistance, Math.abs(dx))

      if (!hasCaptured && movedDistance > DRAG_THRESHOLD) {
        hasCaptured = true
        wrapperRef.current.style.cursor = 'grabbing'
        try {
          event.currentTarget.setPointerCapture(pointerId)
        } catch (e) { /* setPointerCapture can throw in some older browsers - safe to ignore */ }
      }

      if (hasCaptured) {
        wrapperRef.current.scrollLeft = startScrollLeft - dx
      }
    }

    const endDrag = event => {
      if (hasCaptured && wrapperRef.current) {
        wrapperRef.current.style.cursor = 'grab'
        if (pointerId !== null && event && event.currentTarget && event.currentTarget.releasePointerCapture) {
          try { event.currentTarget.releasePointerCapture(pointerId) } catch (e) { /* ignore */ }
        }
      }
      isPressed = false
      hasCaptured = false
      pointerId = null
    }

    // Runs in the CAPTURE phase (before a tab button's own click handler,
    // which fires in the bubble phase), so it can swallow the click that
    // would otherwise fire when the mouse is released - preventing an
    // accidental tab selection at the end of what was really just a
    // drag-to-scroll.
    const onClickCapture = event => {
      if (movedDistance > DRAG_THRESHOLD) {
        event.stopPropagation()
        event.preventDefault()
      }
    }

    return { onPointerDown, onPointerMove, onPointerUp: endDrag, onPointerLeave: endDrag, onClickCapture }
  }

  // Brings a just-clicked tab button fully into view within its scrollable
  // wrapper if it was only partially visible - the plain native DOM API
  // (well-supported, synchronous/instant), not any custom animation.
  scrollTabIntoView = event => {
    if (event && event.currentTarget && typeof event.currentTarget.scrollIntoView === 'function') {
      event.currentTarget.scrollIntoView({ inline: 'nearest', block: 'nearest' })
    }
  }

  handleCompetencyTabClick = (id, name) => event => {
    this.scrollTabIntoView(event)
    this.props.onSelectCompetency(id, name)
  }

  handleSubcompetencyTabClick = (id, name) => event => {
    this.scrollTabIntoView(event)
    this.props.onSelectSubcompetency(id, name)
  }

  // Plain, single-shot click handler for the arrow buttons - nudges the
  // wrapper div's own scrollLeft directly (the wrapper IS the scrollable
  // element - see the SCROLLING doc comment above).
  handleArrowClick = (wrapperRef, amount) => () => {
    if (!wrapperRef.current) return
    if (typeof wrapperRef.current.scrollBy === 'function') {
      wrapperRef.current.scrollBy({ left: amount, behavior: 'smooth' })
    } else {
      wrapperRef.current.scrollLeft += amount
    }
  }

  render() {
    const {
      classes,
      companyValues,
      competencyid,
      subcompetencyid,
      subcompetencies_list
    } = this.props

    const sortedValues = getSortedCompanyValueLabels(companyValues)
    const subListRaw = subcompetencies_list[competencyid] || []
    const subList = Array.isArray(subListRaw) ? subListRaw : Object.values(subListRaw)

    return (
      <div className={classes.root}>
        <div className={classes.tabRow}>
          <IconButton
            size='small'
            className={classes.scrollButton}
            onClick={this.handleArrowClick(this.topTabsWrapperRef, -150)}
            aria-label='Scroll categories left'
          >
            <ChevronLeftIcon className={classes.scrollIcon} />
          </IconButton>

          <div
            ref={this.topTabsWrapperRef}
            className={classes.tabsScrollWrapper}
            role='tablist'
            aria-label='Category tabs'
            onPointerDown={this.topDrag.onPointerDown}
            onPointerMove={this.topDrag.onPointerMove}
            onPointerUp={this.topDrag.onPointerUp}
            onPointerLeave={this.topDrag.onPointerLeave}
            onClickCapture={this.topDrag.onClickCapture}
          >
            {sortedValues.map(v => {
              const label = filterZZZ(v.valueDesc)
              const selected = v.valueID === competencyid
              return (
                <button
                  key={v.valueID}
                  type='button'
                  role='tab'
                  aria-selected={selected}
                  title={label}
                  className={
                    selected
                      ? `${classes.tabButton} ${classes.tabButtonSelected} ${classes.topTabSelectedText}`
                      : classes.tabButton
                  }
                  onClick={this.handleCompetencyTabClick(v.valueID, label)}
                >
                  {label}
                </button>
              )
            })}
          </div>

          <IconButton
            size='small'
            className={classes.scrollButton}
            onClick={this.handleArrowClick(this.topTabsWrapperRef, 150)}
            aria-label='Scroll categories right'
          >
            <ChevronRightIcon className={classes.scrollIcon} />
          </IconButton>
        </div>

        {competencyid ? (
          <div className={classes.subTabsWrap}>
            <div className={classes.tabRow}>
              <IconButton
                size='small'
                className={classes.scrollButton}
                onClick={this.handleArrowClick(this.subTabsWrapperRef, -110)}
                aria-label='Scroll sub-categories left'
              >
                <ChevronLeftIcon className={classes.subScrollIcon} />
              </IconButton>

              <div
                ref={this.subTabsWrapperRef}
                className={classes.tabsScrollWrapper}
                role='tablist'
                aria-label='Sub-category tabs'
                onPointerDown={this.subDrag.onPointerDown}
                onPointerMove={this.subDrag.onPointerMove}
                onPointerUp={this.subDrag.onPointerUp}
                onPointerLeave={this.subDrag.onPointerLeave}
                onClickCapture={this.subDrag.onClickCapture}
              >
                {subList.map(item => {
                  const label = filterZZZ(item.name)
                  const selected = item.id === subcompetencyid
                  return (
                    <button
                      key={item.id}
                      type='button'
                      role='tab'
                      aria-selected={selected}
                      title={label}
                      className={
                        selected
                          ? `${classes.subTabButton} ${classes.tabButtonSelected} ${classes.subTabSelectedText}`
                          : classes.subTabButton
                      }
                      onClick={this.handleSubcompetencyTabClick(item.id, label)}
                    >
                      {label}
                    </button>
                  )
                })}
              </div>

              <IconButton
                size='small'
                className={classes.scrollButton}
                onClick={this.handleArrowClick(this.subTabsWrapperRef, 110)}
                aria-label='Scroll sub-categories right'
              >
                <ChevronRightIcon className={classes.subScrollIcon} />
              </IconButton>
            </div>
          </div>
        ) : null}
      </div>
    )
  }
}

CategoryTabs.propTypes = {
  competencyid: PropTypes.string,
  subcompetencyid: PropTypes.string,
  onSelectCompetency: PropTypes.func.isRequired,
  onSelectSubcompetency: PropTypes.func.isRequired
}

CategoryTabs.defaultProps = {
  competencyid: '',
  subcompetencyid: ''
}

const CategoryTabsStyled = withStyles(styles)(CategoryTabs)

const mapStateToProps = state => {
  return {
    companyValues: state.pldp.companyValues,
    subcompetencies_list: state.subcompetenciesList.subcompetencies_list
  }
}

// No mapDispatchToProps - CategoryTabs is deliberately read-only against
// Redux. It never dispatches getCompanyValues or fetchSubCompetencies;
// it relies on CompetencyOptions / SubCompetencyOptions (siblings inside
// CategoriesSelect) to keep those slices populated and up to date.
export default connect(mapStateToProps)(CategoryTabsStyled)